import { Activity, CalendarDays, Eye, Scale } from "lucide-react";
import { useBrand } from "@/brand";

const benefits = [
  {
    icon: Activity,
    text: "Mindre effekt av kortsiktige svingninger i spotprisen",
  },
  {
    icon: CalendarDays,
    text: "Jevnere prisutvikling og enklere budsjettering over tid",
  },
  {
    icon: Eye,
    text: "Løpende oppfølging av kraftmarkedet, uten at du bruker tid på det",
  },
  {
    icon: Scale,
    text: "En mellomting mellom ren spotpris og fastpris",
  },
];

const details = [
  { label: "Prisen på strømmen", value: "Spotpris med aktiv prissikring" },
  { label: "Påslag og faste beløp", value: "3 øre per kWh + 49 kr fast månedsavgift" },
  { label: "Bindingstid", value: "Står i tilbudet" },
  { label: "Slik bestiller du", value: "Gjennom rådgiver, etter tilbud" },
  { label: "Passer for", value: "Bedrifter som vil la andre følge markedet" },
];

const PriceChart = () => {
  const brand = useBrand();
  return (
  <div className="p-6 rounded-2xl bg-slate-100 border border-slate-200" data-testid="flexsikring-chart">
    <svg viewBox="0 0 600 300" className="w-full h-auto" role="img" aria-label="Illustrasjon av sikret andel versus spotmarkedet">
      <text x="10" y="24" className="fill-slate-500" fontSize="14">Pris</text>
      <text x="10" y="292" className="fill-slate-500" fontSize="14">Tid</text>
      {[70, 120, 170, 220].map((y) => (
        <line key={y} x1="40" y1={y} x2="580" y2={y} stroke="#E2E8F0" strokeWidth="1" />
      ))}
      {/* Sikrede andeler */}
      <rect x="60" y="78" width="150" height="14" rx="3" fill={brand.chartAccent} />
      <rect x="250" y="118" width="150" height="14" rx="3" fill={brand.chartAccent} />
      <rect x="440" y="78" width="130" height="14" rx="3" fill={brand.chartAccent} />
      {/* Spotmarkedet */}
      <path
        d="M40 110 L70 130 L90 100 L120 135 L140 150 L160 210 L200 215 L220 205 L240 150 L260 205 L280 215 L300 170 L320 90 L340 50 L360 120 L380 150 L400 215 L430 225 L450 180 L470 140 L490 165 L510 200 L530 220 L560 225 L580 222"
        fill="none"
        stroke="#0B132B"
        strokeWidth="4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
    <div className="flex items-center gap-6 mt-4 text-sm">
      <span className="flex items-center gap-2">
        <span className="w-6 h-2.5 rounded-sm bg-emerald-500 inline-block" />
        <span className="font-bold text-[#0B132B]">Sikret andel</span>
      </span>
      <span className="flex items-center gap-2">
        <span className="w-6 h-1 rounded-sm bg-[#0B132B] inline-block" />
        <span className="font-bold text-[#0B132B]">Spotmarkedet</span>
      </span>
    </div>
    <p className="text-xs text-slate-500 mt-2">Illustrasjon</p>
  </div>
  );
};

const ForvaltningDetail = () => {
  const brand = useBrand();
  return (
  <section id="flexsikring" className="py-24 bg-white border-b border-slate-200" data-testid="flexsikring-section">
    <div className="max-w-7xl mx-auto px-6">
      {/* Introduksjon */}
      <div className="max-w-3xl mx-auto text-center mb-16">
        <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
          Strømavtaler / Flexsikring
        </span>
        <h2 className="font-serif-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mt-2 tracking-tight">
          {brand.name} Flexsikring – spotpris med aktiv prissikring
        </h2>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Flexsikring er strøm til spotpris, der {brand.name} i tillegg sikrer
          deler av forbruket ditt i forkant. Målet er en jevnere prisutvikling
          enn ren spotpris, uten at du må følge kraftmarkedet selv.
        </p>
        <a
          href="#kontakt"
          className="inline-flex mt-8 px-8 py-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-[#0B132B] font-bold text-base transition-colors shadow-lg"
          data-testid="flexsikring-cta-button"
        >
          Få tilbud
        </a>
      </div>

      {/* Slik fungerer det */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
        <div>
          <h3 className="font-serif-heading text-2xl sm:text-3xl font-bold text-[#0B132B] mb-6">
            Slik fungerer det
          </h3>
          <div className="space-y-4 text-slate-600 leading-relaxed text-sm">
            <p>
              Strømmen din avregnes til spotpris. Samtidig kjøper {brand.name}{" "}
              inn deler av forbruksvolumet ditt på forhånd, på tidspunkter der
              vi vurderer markedet som gunstig – gjennom Nasdaq og Nordpool.
              Den sikrede andelen får dermed en kjent pris, mens resten følger
              spotmarkedet.
            </p>
            <p>
              {brand.name} tar beslutningene om når, hvor mye og hvor lenge det
              sikres, basert på løpende vurdering av markedet. Sikringen kan
              gjøres for din bedrift alene eller sammen med andre{" "}
              {brand.name}-kunder i samme prisområde. Slik får også små og
              mellomstore bedrifter prissikring gjennom et større samlet volum.
            </p>
            <p>
              Jo lengre tidshorisont vi har til å planlegge kjøpene, jo bedre
              uttelling vil bedriften din generelt få. Vi tror på dialog og
              inkludering i prosessen for deg som er mer interessert i hvordan
              handel av strøm fungerer – ta gjerne kontakt for en uforpliktende
              prat.
            </p>
          </div>
        </div>
        <PriceChart />
      </div>

      {/* Hva Forvaltning kan gi */}
      <div className="mb-20">
        <h3 className="font-serif-heading text-2xl sm:text-3xl font-bold text-[#0B132B] mb-10 text-center">
          Hva Flexsikring kan gi
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b) => (
            <div
              key={b.text}
              className="p-6 rounded-xl bg-slate-50 border border-slate-200 border-l-4 border-l-emerald-500 flex flex-col gap-4"
              data-testid={`benefit-card-${b.text.toLowerCase().slice(0, 20).replace(/\s+/g, "-")}`}
            >
              <div className="w-12 h-12 rounded-lg bg-[#0B132B] flex items-center justify-center text-emerald-400 shrink-0">
                <b.icon className="w-6 h-6" aria-hidden="true" />
              </div>
              <p className="text-sm font-bold text-[#0B132B] leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Avtaledetaljer */}
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl bg-white border border-slate-200 border-t-4 border-t-emerald-500 shadow-lg p-8" data-testid="flexsikring-details-card">
          <h3 className="text-2xl font-bold text-[#0B132B] pb-4 border-b-2 border-[#0B132B]">
            Flexsikring
          </h3>
          <dl>
            {details.map((d) => (
              <div key={d.label} className="py-4 border-b border-slate-100 last:border-b-0">
                <dt className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {d.label}
                </dt>
                <dd className="text-base font-medium text-[#0B132B] mt-1">{d.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  </section>
  );
};

export default ForvaltningDetail;
